import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';

import MfaChallengeModal from 'components/layout/MfaChallengeModal';
import MfaEnrollmentModal from 'components/layout/MfaEnrollmentModal';
import {
  extractMfaChallenge,
  setMfaRunner,
  VERIFIED_CHALLENGE_HEADER,
} from 'util/mfaChallenge';
import { getMFAAuthenticators } from 'util/rehive';
import { configAuthSelector } from 'redux/rehive/selectors';
import { tokenSelector } from 'redux/auth/selectors';

// Generalised in-flow MFA challenge handling.
//
// Any flow can opt in by wrapping its API call with `runWithMfa`:
//
//   const { runWithMfa } = useMfaChallenge();
//   const res = await runWithMfa(opts => createDebit(payload, opts));
//
// The thunk receives an options object that it forwards to the service function
// (which forwards it to the SDK / callApi). On the first attempt no options are
// passed; if the call fails with an MFA challenge, the modal collects + verifies
// an OTP, then the SAME thunk is re-invoked with the `Verified-Challenge` header
// so the request resumes transparently. Non-MFA errors bubble unchanged.
//
// The provider also exposes `ensureMfaEnrolled()`, a pre-submit gate that forces
// MFA enrollment (via MfaEnrollmentModal) before a privileged action when the
// tenant requires MFA but the user has no verified authenticator.
const MfaChallengeContext = createContext(null);

export function MfaChallengeProvider({ children }) {
  const authConfig = useSelector(configAuthSelector);
  const token = useSelector(tokenSelector);

  const [challenge, setChallenge] = useState(null);
  // Holds the resolve/reject of the promise returned by solveChallenge while the
  // modal is open, so the modal's success/cancel can settle the awaiting caller.
  const pendingRef = useRef(null);

  const solveChallenge = useCallback(activeChallenge => {
    return new Promise((resolve, reject) => {
      pendingRef.current = { resolve, reject };
      setChallenge(activeChallenge);
    });
  }, []);

  const handleSuccess = useCallback(() => {
    const pending = pendingRef.current;
    pendingRef.current = null;
    setChallenge(null);
    pending?.resolve();
  }, []);

  const handleCancel = useCallback(() => {
    const pending = pendingRef.current;
    pendingRef.current = null;
    setChallenge(null);
    const err = new Error('mfa_challenge_cancelled');
    err.mfaCancelled = true;
    pending?.reject(err);
  }, []);

  // Enrollment blocker plumbing — mirrors the challenge modal above, but opens the
  // MFA setup form (MfaEnrollmentModal) instead of an OTP prompt.
  const [enrollmentOpen, setEnrollmentOpen] = useState(false);
  const enrollmentPendingRef = useRef(null);

  const solveEnrollment = useCallback(() => {
    return new Promise((resolve, reject) => {
      enrollmentPendingRef.current = { resolve, reject };
      setEnrollmentOpen(true);
    });
  }, []);

  const handleEnrollmentSuccess = useCallback(() => {
    const pending = enrollmentPendingRef.current;
    enrollmentPendingRef.current = null;
    setEnrollmentOpen(false);
    pending?.resolve();
  }, []);

  const handleEnrollmentCancel = useCallback(() => {
    const pending = enrollmentPendingRef.current;
    enrollmentPendingRef.current = null;
    setEnrollmentOpen(false);
    const err = new Error('mfa_challenge_cancelled');
    err.mfaCancelled = true;
    pending?.reject(err);
  }, []);

  // Pre-submit gate: when the tenant requires MFA (and the transaction challenge
  // feature is enabled) but the current user has no verified authenticator, block
  // with the enrollment modal until they enrol. Resolves once enrolled; rejects
  // with an `mfaCancelled` error if the user dismisses the modal.
  const ensureMfaEnrolled = useCallback(async () => {
    if (!authConfig?.transactionChallengeEnabled) return; // feature off for tenant
    const mfaRequired = authConfig?.mfa && authConfig.mfa !== 'optional';
    if (!mfaRequired) return; // company doesn't force it
    const resp = await getMFAAuthenticators('verified=1');
    const enrolled = resp?.data?.results?.some(a => a.verified);
    if (enrolled) return; // already enrolled
    await solveEnrollment();
  }, [authConfig, solveEnrollment]);

  const runWithMfa = useCallback(
    async thunk => {
      try {
        return await thunk();
      } catch (err) {
        const activeChallenge = extractMfaChallenge(err);
        if (!activeChallenge) {
          throw err;
        }
        await solveChallenge(activeChallenge); // resolves once verified, rejects on cancel
        return thunk({
          headers: { [VERIFIED_CHALLENGE_HEADER]: activeChallenge.id },
        });
      }
    },
    [solveChallenge],
  );

  // Expose runWithMfa to non-React callers (util/api.js handlers, page-config
  // onSubmits, class components) via the module bridge. See util/mfaChallenge.js.
  useEffect(() => {
    setMfaRunner(runWithMfa);
    return () => setMfaRunner(null);
  }, [runWithMfa]);

  const value = useMemo(
    () => ({ runWithMfa, solveChallenge, ensureMfaEnrolled }),
    [runWithMfa, solveChallenge, ensureMfaEnrolled],
  );

  return (
    <MfaChallengeContext.Provider value={value}>
      {children}
      <MfaChallengeModal
        challenge={challenge}
        onSuccess={handleSuccess}
        onCancel={handleCancel}
      />
      <MfaEnrollmentModal
        open={enrollmentOpen}
        token={token}
        authConfig={authConfig}
        onSuccess={handleEnrollmentSuccess}
        onCancel={handleEnrollmentCancel}
      />
    </MfaChallengeContext.Provider>
  );
}

export function useMfaChallenge() {
  const ctx = useContext(MfaChallengeContext);
  if (!ctx) {
    throw new Error('useMfaChallenge must be used within MfaChallengeProvider');
  }
  return ctx;
}

// HOC for class components: injects `runWithMfa` as a prop.
export function withMfaChallenge(Component) {
  function WithMfaChallenge(props) {
    const { runWithMfa } = useMfaChallenge();
    return <Component {...props} runWithMfa={runWithMfa} />;
  }
  WithMfaChallenge.displayName = `withMfaChallenge(${Component.displayName ||
    Component.name ||
    'Component'})`;
  return WithMfaChallenge;
}
